import { useRef } from "react";
import type { ReactNode } from "react";
import { Paperclip } from "lucide-react";
import { Text } from "@/components/Typography";
import { Button } from "@/components/Button";
import { SendMessageIcon } from "@/components/icons/icons";
import { PendingAttachments } from "@/components/MessageAttachments";
import { RichMessageInput } from "@/components/RichMessageInput";
import type { ProgramResource } from "@/services/api";
import { RESOURCE_ACCEPT_ATTR } from "@/utils/documentValidation";

/**
 * The message input, shared by all three composers.
 *
 * MessagesDrawer (group + tabs), DirectPabMessageDrawer and
 * DirectProgramMessageDrawer previously each carried their own copy of this
 * footer. They had already drifted — the send-enabled rule, the paperclip and
 * the attachment strip only existed in one of them — so anything added here had
 * to be added three times and remembered three times.
 *
 * The text area itself is RichMessageInput (the Gmail-style formatting
 * toolbar). Its value is still a plain string — formatting is carried as the
 * lightweight markdown in utils/messageMarkdown — so `value`/`onChange` mean
 * exactly what they did when this was a <textarea>, and no caller changed.
 *
 * The recipient picker is NOT part of this: only the group drawer has one, so it
 * comes in through `header`.
 */
export interface MessageComposerProps {
  value: string;
  onChange: (value: string) => void;
  onSend: () => void;
  sending?: boolean;
  /** Whole composer unusable — e.g. no conversation selected. */
  disabled?: boolean;
  placeholder?: string;

  /** Files staged for this message (see useMessageAttachments). */
  attachments?: ProgramResource[];
  /** Omit to hide the paperclip entirely. */
  onAttachFiles?: (files: FileList | null) => void;
  onRemoveAttachment?: (id: number) => void;
  isUploading?: boolean;
  /** False while the conversation (and so the programme) isn't known yet. */
  canAttach?: boolean;

  /** Rendered above the input — the group drawer's "To:" picker. */
  header?: ReactNode;

  /** Focus the input on mount, for drawers that open straight onto it. */
  autoFocus?: boolean;
}

export const MessageComposer = ({
  value,
  onChange,
  onSend,
  sending = false,
  disabled = false,
  placeholder = "Type a message...",
  attachments = [],
  onAttachFiles,
  onRemoveAttachment,
  isUploading = false,
  canAttach = true,
  header,
  autoFocus = false,
}: MessageComposerProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  // A staged attachment is enough on its own: sending a file with no
  // accompanying text is normal, and requiring text made that impossible.
  const hasContent = !!value.trim() || attachments.length > 0;
  const canSend = hasContent && !sending && !isUploading && !disabled;

  return (
    <div className="px-6 pt-4 pb-6 border-t border-theme-border flex-shrink-0">
      {header}

      <PendingAttachments
        pending={attachments}
        uploading={isUploading}
        onRemove={(id) => onRemoveAttachment?.(id)}
      />

      <div className="flex items-end gap-3">
        <RichMessageInput
          className="flex-1 min-w-0"
          value={value}
          onChange={onChange}
          onSubmit={() => {
            if (canSend) onSend();
          }}
          placeholder={placeholder}
          disabled={disabled}
          autoFocus={autoFocus}
          /* The paperclip joins the input's own action row, next to the
             formatting toggle and emoji — the three sit at the trailing edge
             of the text rather than one of them floating over it. */
          trailingActions={
            onAttachFiles ? (
              <>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept={RESOURCE_ACCEPT_ATTR}
                  className="hidden"
                  onChange={(e) => {
                    onAttachFiles(e.target.files);
                    // Reset so re-picking the same file still fires onChange.
                    e.target.value = "";
                  }}
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={disabled || isUploading || !canAttach}
                  aria-label="Attach a file"
                  title="Attach a file"
                  className="rich-msg-tool"
                >
                  <Paperclip size={16} />
                </button>
              </>
            ) : null
          }
        />
        <Button
          type="button"
          variant="ghost"
          onClick={onSend}
          disabled={!canSend}
          aria-label="Send message"
          className="flex items-center justify-center w-[52px] h-[46px] rounded-[14px] p-0 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed bg-theme-msg-bubble-self-bg hover:bg-theme-msg-send-btn-hover flex-shrink-0 border border-theme-msg-send-btn-border"
        >
          <span className="inline-flex items-center justify-center w-5 h-5 text-theme-msg-bubble-self-name">
            <SendMessageIcon size={18} />
          </span>
        </Button>
      </div>
      <Text as="p" className="font-inter text-12 mt-2 text-theme-text-muted">
        Press Enter to send · Shift + Enter for a new line
      </Text>
    </div>
  );
};
